<template>
  <div>
    <page-header title="作品运营数据" description="查看各平台作品的公开指标和更新情况">
      <el-button type="primary" @click="manageAccounts">自动采集账号</el-button>
      <el-button @click="create">补充录入作品</el-button>
      <el-button @click="importing=true">导入 Excel</el-button>
      <el-button @click="runs=true">运行记录</el-button>
    </page-header>
    <el-tabs v-model="activeTab" class="creator-tabs" @tab-click="handleTabClick">
      <el-tab-pane label="作品列表" name="contents">
        <el-alert class="mb" type="info" :closable="false" show-icon title="更新说明" description="配置 B站、抖音或公众号账号后，系统每天发现新增作品，再刷新该平台全部已入库作品的公开指标；手动更新仍仅支持当天数据。"/>
        <el-form inline class="filter-bar">
          <el-form-item>
            <el-select v-model="platform" clearable placeholder="全部平台" :disabled="hasActiveRefresh">
              <el-option v-for="x in platforms" :key="x.value" :label="x.label" :value="x.value"/>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select v-model="status" clearable placeholder="全部状态" class="status-select" :disabled="hasActiveRefresh">
              <el-option v-for="x in statuses" :key="x.value" :label="x.label" :value="x.value"/>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-date-picker v-model="updatedDate" type="date" value-format="yyyy-MM-dd" placeholder="数据日期" :clearable="false" :disabled="hasActiveRefresh"/>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" plain :loading="loading" :disabled="hasActiveRefresh" @click="search">{{loading?'查询中':'查询'}}</el-button>
          </el-form-item>
          <el-form-item>
            <el-button :loading="refreshingPlatform==='bilibili'" :disabled="!canRefreshToday||hasActiveRefresh" :title="refreshHint" @click="refreshPlatform('bilibili')">{{refreshingPlatform==='bilibili'?'B站更新中':'更新 B站'}}</el-button>
            <el-button :loading="refreshingPlatform==='douyin'" :disabled="!canRefreshToday||hasActiveRefresh" :title="refreshHint" @click="refreshPlatform('douyin')">{{refreshingPlatform==='douyin'?'抖音更新中':'更新抖音'}}</el-button>
            <el-button :loading="refreshingPlatform==='wechat_mp'" :disabled="!canRefreshToday||hasActiveRefresh" :title="refreshHint" @click="refreshPlatform('wechat_mp')">{{refreshingPlatform==='wechat_mp'?'公众号更新中':'更新公众号'}}</el-button>
            <el-button v-if="supportsLatestComments" :loading="!!refreshingComments" :disabled="hasActiveRefresh" @click="refreshLatestComments">
              {{refreshingComments?'评论采集中':'采集作品评论'}}
            </el-button>
            <el-button v-if="platform === 'douyin'" type="danger" plain :loading="deletingLegacy" :disabled="hasActiveRefresh" @click="deleteDouyinLegacyMetrics">{{deletingLegacy?'删除中':'删除抖音旧指标'}}</el-button>
            <el-button v-if="canAssociatePlatform" :loading="associating" :disabled="hasActiveRefresh" @click="associatePlatformContents">{{associating?'关联中':'一键关联当前平台作品'}}</el-button>
          </el-form-item>
        </el-form>
        <el-card v-loading="loading||!!refreshingPlatform" :element-loading-text="tableLoadingText">
          <el-table :data="items" :empty-text="tableEmptyText">
            <el-table-column type="index" :index="index" label="序号" width="65" align="center"/>
            <el-table-column label="作品标题" min-width="260">
              <template slot-scope="s">
                <a v-if="isURL(s.row.source_url)" :href="s.row.source_url" target="_blank">{{s.row.title}}</a>
                <span v-else>{{s.row.title}}</span>
                <div class="muted">{{s.row.source_id}}</div>
              </template>
            </el-table-column>
            <el-table-column label="平台" width="100"><template slot-scope="s"><platform-tag :value="s.row.platform"/></template></el-table-column>
            <el-table-column label="发布日期" width="120"><template slot-scope="s">{{dateOnly(s.row.published_at)}}</template></el-table-column>
            <el-table-column label="更新日期" width="165"><template slot-scope="s">{{formatDate(s.row.last_captured_at)||'暂未更新'}}</template></el-table-column>
            <el-table-column label="最新公开指标" min-width="180"><template slot-scope="s"><metric-list :metrics="s.row.latest_metrics"/></template></el-table-column>
            <el-table-column label="状态" width="165">
              <template slot-scope="s">
                <el-tag v-if="isRefreshing(s.row)" type="info"><i class="el-icon-loading"/> 正在更新</el-tag>
                <el-tag v-else-if="s.row.last_status==='failed'" type="danger" :title="s.row.last_error||''">更新失败</el-tag>
                <el-tag v-else-if="s.row.last_status==='partial_success'" type="warning" :title="s.row.last_error||''">部分更新</el-tag>
                <el-tag v-else-if="updatedDate&&updatedOnDate(s.row.last_captured_at)" type="success">已更新</el-tag>
                <span v-else-if="updatedDate" class="failed">未在 {{updatedDate}} 更新</span>
                <el-tag v-else-if="s.row.last_status==='success'" type="success">已更新</el-tag>
                <el-tag v-else type="info">等待更新</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="230">
              <template slot-scope="s">
                <el-button type="text" @click="openMetrics(s.row)">趋势</el-button>
                <el-button type="text" @click="openComments(s.row)">评论</el-button>
                <el-button type="text" @click="edit(s.row)">编辑资料</el-button>
                <el-button v-if="supportsRefresh(s.row)" type="text" :loading="isRefreshing(s.row)" :disabled="!canRefreshToday||hasActiveRefresh" :title="refreshHint" @click="refresh(s.row)">{{isRefreshing(s.row)?'更新中':'更新'}}</el-button>
                <el-button type="text" class="danger" :loading="isDeleting(s.row)" :disabled="hasActiveRefresh" @click="remove(s.row)">{{isDeleting(s.row)?'删除中':'删除'}}</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination">
            <el-pagination background layout="total, prev, pager, next, jumper" :current-page="page" :page-size="pageSize" :total="total" @current-change="changePage"/>
          </div>
        </el-card>
      </el-tab-pane>
      <el-tab-pane label="评论总量变化" name="comments">
        <el-form inline class="filter-bar statistics-filter">
          <el-form-item label="统计日期">
            <el-date-picker v-model="statisticsDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" :clearable="false"/>
          </el-form-item>
          <el-form-item label="平台">
            <el-select v-model="statisticsPlatform" clearable placeholder="全部平台">
              <el-option v-for="x in platforms" :key="x.value" :label="x.label" :value="x.value"/>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="statisticsLoading" :disabled="statisticsLoading" @click="loadStatistics">{{statisticsLoading?'查询中':'查询'}}</el-button>
          </el-form-item>
        </el-form>
        <creator-comment-statistics
          ref="commentStatistics"
          @loading-change="statisticsLoading=$event"
          @open-comments="openStatisticsComments"
        />
      </el-tab-pane>
    </el-tabs>
    <creator-form-dialog :visible.sync="formVisible" :editing="editing" @success="search"/>
    <creator-run-dialogs :visible.sync="runs"/>
    <creator-metrics-drawer :visible.sync="metricsVisible" :item="metricsItem"/>
    <creator-comments-drawer :visible.sync="commentsVisible" :item="commentsItem"/>
    <el-dialog title="导入作品链接 Excel" :visible.sync="importing" width="460px" :close-on-click-modal="!uploading" :close-on-press-escape="!uploading" :show-close="!uploading">
      <el-upload :auto-upload="false" :limit="1" accept=".xlsx" :disabled="uploading" :on-change="f=>file=f.raw"><el-button :disabled="uploading">选择 Excel</el-button></el-upload>
      <span slot="footer"><el-button :disabled="uploading" @click="closeImport">取消</el-button><el-button type="primary" :loading="uploading" @click="upload">{{uploading?'导入中':'导入'}}</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'
import PageHeader from '../components/PageHeader.vue'
import PlatformTag from '../components/PlatformTag.vue'
import MetricList from '../components/MetricList.vue'
import CreatorFormDialog from './CreatorFormDialog.vue'
import CreatorRunDialogs from './CreatorRunDialogs.vue'
import CreatorMetricsDrawer from './CreatorMetricsDrawer.vue'
import CreatorCommentsDrawer from './CreatorCommentsDrawer.vue'
import CreatorCommentStatistics from './CreatorCommentStatistics.vue'

export default {
  components: { PageHeader, PlatformTag, MetricList, CreatorFormDialog, CreatorRunDialogs, CreatorMetricsDrawer, CreatorCommentsDrawer, CreatorCommentStatistics },
  data () {
    return {
      activeTab: 'contents', statisticsPlatform: '', statisticsDate: '', statisticsLoaded: false, statisticsLoading: false,
      items: [], platform: '', status: '', updatedDate: '', loading: false, refreshingPlatform: '', refreshingComments: '', destroyed: false, refreshingRows: {}, deletingRows: {}, associating: false, deletingLegacy: false, uploading: false, formVisible: false, editing: null, runs: false, importing: false, file: null, metricsVisible: false, metricsItem: null, commentsVisible: false, commentsItem: null,
      page: 1, pageSize: 20, total: 0,
      platforms: [{ value: 'bilibili', label: 'B站' }, { value: 'douyin', label: '抖音' }, { value: 'xiaohongshu', label: '小红书' }, { value: 'wechat_channels', label: '视频号' }, { value: 'wechat_mp', label: '公众号' }],
      statuses: [{ value: 'success', label: '已更新' }, { value: 'partial_success', label: '部分更新' }, { value: 'failed', label: '更新失败' }, { value: 'pending', label: '未更新' }]
    }
  },
  computed: {
    canRefreshToday () { return this.updatedDate === this.today() },
    refreshHint () { return this.canRefreshToday ? '' : '仅可更新当天数据，请选择今天' },
    canAssociatePlatform () { return ['bilibili', 'douyin', 'wechat_mp'].includes(this.platform) },
    supportsLatestComments () {
      return ['douyin', 'bilibili', 'xiaohongshu', 'wechat_channels', 'wechat_mp'].includes(this.platform)
    },
    hasActiveRefresh () { return !!this.refreshingPlatform || !!this.refreshingComments || Object.keys(this.refreshingRows).length > 0 },
    tableEmptyText () { return this.platform || this.status ? '没有符合当前筛选条件的作品' : '暂无作品' },
    tableLoadingText () {
      if (this.refreshingPlatform === 'bilibili') return '正在更新 B站全部作品，请稍候…'
      if (this.refreshingPlatform === 'douyin') return '正在更新抖音全部作品，请稍候…'
      if (this.refreshingPlatform === 'wechat_mp') return '正在更新公众号全部文章，请稍候…'
      return '正在加载作品…'
    }
  },
  mounted () {
    this.updatedDate = this.today()
    this.statisticsDate = this.today()
    this.$nextTick(() => this.search())
  },
  beforeDestroy () { this.destroyed = true },
  methods: {
    today () { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` },
    yesterday () {
      const date = new Date()
      date.setDate(date.getDate() - 1)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    async load () {
      this.loading = true
      try {
        const params = [`page=${this.page}`, `page_size=${this.pageSize}`]
        if (this.platform) params.push(`platform=${encodeURIComponent(this.platform)}`)
        if (this.status) params.push(`status=${encodeURIComponent(this.status)}`)
        if (this.status && this.updatedDate) params.push(`updated_date=${encodeURIComponent(this.updatedDate)}`)
        const result = await yuqingApi.get(`creator-content?${params.join('&')}`)
        this.items = result.items || []
        this.total = result.total || 0
      } catch (e) { this.$message.error(e.message) } finally { this.loading = false }
    },
    search () {
      this.page = 1
      return this.load()
    },
    handleTabClick (tab) {
      if (tab.name === 'comments' && !this.statisticsLoaded) this.loadStatistics()
    },
    loadStatistics () {
      if (!this.statisticsDate) return this.$message.warning('请选择统计日期')
      this.statisticsLoaded = true
      this.$nextTick(() => {
        if (this.$refs.commentStatistics) {
          this.$refs.commentStatistics.load(this.statisticsDate, this.statisticsPlatform)
        }
      })
    },
    reloadStatistics () {
      if (this.statisticsLoaded && this.statisticsDate === this.today()) this.loadStatistics()
    },
    changePage (page) { this.page = page; this.load() },
    index (index) { return (this.page - 1) * this.pageSize + index + 1 },
    create () { this.editing = null; this.formVisible = true },
    manageAccounts () { this.$router.push({ path: '/yuqing/system', query: { tab: 'creator-account' } }) },
    async associatePlatformContents () {
      if (this.associating || this.hasActiveRefresh) return
      try {
        this.associating = true
        const result = await yuqingApi.get(`creator-accounts?platform=${encodeURIComponent(this.platform)}`)
        const account = (result.items || [])[0]
        if (!account) return this.$message.warning('请先配置当前平台的自动采集账号')
        const platformName = { bilibili: 'B站', douyin: '抖音', wechat_mp: '公众号' }[this.platform] || this.platform
        await this.$confirm(`将当前筛选的${platformName}作品全部关联到“${account.account_name || account.account_id}”吗？`, '一键关联作品', { type: 'warning' })
        const association = (await yuqingApi.post(`creator-accounts/${account.id}/associate-contents`, {})).association || {}
        this.$message.success(`已关联 ${association.associated || 0} 条作品`)
        await this.load()
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e.message || '关联失败')
      } finally {
        this.associating = false
      }
    },
    async remove (row) {
      if (!row || this.isDeleting(row) || this.hasActiveRefresh) return
      try {
        await this.$confirm(`删除“${row.title || row.source_id}”及其全部运营指标吗？此操作不可恢复。`, '删除作品', { type: 'warning' })
        this.$set(this.deletingRows, row.id, true)
        const result = await yuqingApi.delete(`creator-content/${row.id}`)
        this.$message.success(`已删除 ${result.deleted || 0} 条作品及 ${result.deleted_metrics || 0} 条指标`)
        await this.load()
        this.reloadStatistics()
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e.message || '删除失败')
      } finally {
        this.$delete(this.deletingRows, row.id)
      }
    },
    async deleteDouyinLegacyMetrics () {
      if (this.deletingLegacy || this.hasActiveRefresh) return
      try {
        await this.$confirm('将删除抖音历史旧指标（douyin_like_count、douyin_comment_count、douyin_collect_count、douyin_share_count）。作品和当前指标不会删除，此操作不可恢复。是否继续？', '删除抖音旧指标', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' })
        this.deletingLegacy = true
        const result = await yuqingApi.delete('creator-content/metrics/legacy/douyin')
        this.$message.success(`已删除 ${result.deleted_metrics || 0} 条旧指标，影响 ${result.affected_contents || 0} 条作品`)
        await this.load()
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e.message || '删除失败')
      } finally {
        this.deletingLegacy = false
      }
    },
    edit (row) { this.editing = row; this.formVisible = true },
    openMetrics (row) { this.metricsItem = row; this.metricsVisible = true },
    openComments (row) { this.commentsItem = row; this.commentsVisible = true },
    openStatisticsComments (row) {
      const metricKeys = {
        xiaohongshu: 'xhs_comment_count',
        wechat_channels: 'wechat_channels_comment_count'
      }
      const metricKey = metricKeys[row.platform] || 'comment_count'
      this.commentsItem = {
        id: row.content_id,
        platform: row.platform,
        source_id: row.source_id,
        source_url: row.source_url,
        title: row.title,
        metric_date: row.metric_date,
        latest_metrics: row.current_count === null || row.current_count === undefined
          ? []
          : [{ metric_key: metricKey, value: row.current_count, availability: 'available' }]
      }
      this.commentsVisible = true
    },
    updatedOnDate (value) { return !!(value && this.updatedDate && String(value).slice(0, 10) === this.updatedDate) },
    supportsRefresh (row) { return ['bilibili', 'douyin', 'wechat_mp'].includes(row.platform) },
    isRefreshing (row) { return !!(row && this.refreshingRows[row.id]) },
    isDeleting (row) { return !!(row && this.deletingRows[row.id]) },
    dateOnly (v) { return v && !String(v).startsWith('0001-01-01') ? String(v).slice(0, 10) : '-' },
    formatDate (v) { return v ? String(v).replace('T', ' ').slice(0, 16) : '' },
    isURL (v) { try { const u = new URL(v); return u.protocol === 'http:' || u.protocol === 'https:' } catch (e) { return false } },
    refreshMessage (run) {
      const total = Number(run && run.total_count) || 0
      const succeeded = Number(run && run.succeeded_count) || 0
      const failed = Number(run && run.failed_count) || 0
      const partial = Number(run && run.partial_count) || 0
      const counts = `共 ${total} 条，成功 ${succeeded} 条${partial ? `（其中部分更新 ${partial} 条）` : ''}，失败 ${failed} 条`
      const reason = run && run.failure_reason ? `，原因：${run.failure_reason}` : ''
      if (failed && succeeded) this.$message.warning(`部分更新完成：${counts}${reason}`)
      else if (failed) this.$message.error(`更新失败：${counts}${reason}`)
      else if (partial) this.$message.warning(`更新完成：${counts}，播放量未更新的作品已保留上次有效值`)
      else this.$message.success(`更新完成：${counts}`)
    },
    async refresh (row) {
      if (!row || this.hasActiveRefresh) return
      this.$set(this.refreshingRows, row.id, true)
      try {
        const result = await yuqingApi.post(`creator-content/${row.id}/refresh`, {})
        this.refreshMessage(result.run)
        await this.load()
        this.reloadStatistics()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.$delete(this.refreshingRows, row.id)
      }
    },
    async refreshPlatform (platform) {
      if (this.hasActiveRefresh) return
      this.refreshingPlatform = platform
      try {
        const result = await yuqingApi.post('creator-content/refresh', { platform })
        this.refreshMessage(result.run)
        await this.load()
        this.reloadStatistics()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.refreshingPlatform = ''
      }
    },
    async refreshLatestComments () {
      if (!this.supportsLatestComments || this.hasActiveRefresh) return
      const platform = this.platform
      this.refreshingComments = platform
      try {
        const started = await yuqingApi.post('creator-content/comments/latest/refresh', { platform })
        const runId = started.comment_collection && started.comment_collection.run_id
        if (!runId) throw new Error('评论采集任务启动失败：未返回任务编号')
        this.$message.info('作品评论采集任务已启动，正在逐篇完整采集一级评论及回复')
        const run = await this.waitCommentRun(runId)
        if (!run) {
          this.$message.info('评论仍在后台采集，可在“运行记录”查看进度')
          return
        }
        const text = `共 ${run.total_count || 0} 篇，成功 ${run.succeeded_count || 0} 篇，失败 ${run.failed_count || 0} 篇`
        if (run.status === 'completed') this.$message.success(`作品评论采集完成：${text}`)
        else if (run.status === 'partially_completed') this.$message.warning(`作品评论部分采集完成：${text}，失败原因请查看运行记录`)
        else this.$message.error(`作品评论采集失败：${text}，原因请查看运行记录`)
      } catch (e) {
        this.$message.error(e.message || '作品评论采集失败')
      } finally {
        this.refreshingComments = ''
      }
    },
    async waitCommentRun (runId) {
      for (let i = 0; i < 300 && !this.destroyed; i++) {
        const result = await yuqingApi.get(`creator-content/runs/${encodeURIComponent(runId)}`)
        const run = result.run || {}
        if (['completed', 'partially_completed', 'failed'].includes(run.status)) return run
        await new Promise(resolve => setTimeout(resolve, 2000))
      }
      return null
    },
    async upload () {
      if (this.uploading) return
      if (!this.file) return this.$message.warning('请选择 Excel')
      this.uploading = true
      try {
        const f = new FormData()
        f.append('file', this.file)
        await yuqingApi.upload('creator-content/import', f)
        this.$message.success('作品已导入')
        this.importing = false
        this.file = null
        await this.search()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.uploading = false
      }
    },
    closeImport () {
      if (this.uploading) return
      this.importing = false
      this.file = null
    }
  }
}
</script>

<style scoped>
.mb { margin-bottom: 16px }
.creator-tabs { margin-top: 4px }
.creator-tabs >>> .el-tabs__header { margin-bottom: 16px }
.creator-tabs >>> .el-tabs__item { height: 44px; padding: 0 24px; font-size: 15px; font-weight: 500; line-height: 44px }
.filter-bar { padding: 12px 16px 4px; margin-bottom: 16px; background: #f7f8fa; border: 1px solid #ebeef5; border-radius: 4px }
.filter-bar >>> .el-form-item { margin-bottom: 8px }
.status-select { width: 150px }
.statistics-filter { display: flex; flex-wrap: wrap; align-items: center }
.muted { color: #909399; font-size: 12px; margin-top: 4px }
.failed { color: #f56c6c; font-size: 12px }
.danger { color: #f56c6c }
.pagination { display: flex; justify-content: flex-end; margin-top: 16px }
</style>
